<template>
  <dashboard-main-layout>
    <div class="pa-4">
      <my-dashboard-component
        class="mb-4"
        :installations="sitesLive"
        :sites-live="sitesLive"
        :allGroups="allGroups"
        @add-audit="toggleCreate"
        @import-excel="importDialog = true"
        @add-group="toggleGroup"
        @rename-group="toggleRenameGroup"
        @delete-group="deleteGroup"
        @add-install-to-group="addInstallToGroup"
        @remove-install-from-group="removeInstallFromGroup"
      />

      <v-card
        v-if="sitesLive.length > 0"
        elevation="0"
        class="mb-6 d-flex justify-center"
        style="border-radius: 999px !important"
      >
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('_recherche_')"
          solo
          flat
          hide-details
          clearable
          class="search"
        />
      </v-card>

      <v-data-table
        v-model="selectedInstalls"
        :headers="selectedColumnsOrdered"
        :items="sitesLive"
        :search="search"
        :loading="loading"
        item-key="id"
        show-select
        :footer-props="{
          'items-per-page-options': [20, 50, 100, -1],
          'items-per-page-text': $t('_lignes_par_page_'),
        }"
        :items-per-page="20"
        id="level-1-table"
        v-if="sitesLive.length > 0"
      >
        <template v-slot:item="{ item, isSelected, select }">
          <tr           
          :class="{ disabled: item.is_forbidden }"
          :style="{ cursor: 'pointer' }"
          @click="goToInstallation(item)"          
          >
            <!-- ✅ Checkbox column -->
            <td @click.stop >
              <v-simple-checkbox
                :value="isSelected"
                @input="select"
                :disabled="
                  getStatusChipData(item).text === 'Erreur d\'initialisation'
                "
              />
            </td>
            <td v-for="column in selectedColumnsOrdered" :key="column.key">
              <template
                v-if="
                  column.key !== 'actions' &&
                  column.key !== 'status' &&
                  (item[column.key] === null || item[column.key] === undefined)
                "
              >
                -
              </template>

              <div v-else-if="column.key === 'status'">
                <v-chip :color="getStatusChipData(item).color" dark small>
                  {{ getStatusChipData(item).text }}
                </v-chip>
              </div>

              <div v-else-if="column.key === 'actions'">
                <v-btn
                  v-if="
                    getStatusChipData(item).text === 'Erreur d\'initialisation'
                  "
                  color="warning"
                  small
                  text
                  @click.stop="reprendreBrouillon(item)"
                >
                  {{ $t("_modifier_") }}
                </v-btn>
                <v-menu v-else offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      small
                      text
                      v-bind="attrs"
                      v-on="on"
                      @click.stop
                    >
                      {{ $t("_actions_") }}
                      <v-icon right small>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense>
                    <v-list-item link @click="/* supprimer */">
                      <v-list-item-title>
                        {{ $t("_supprimer_") }}
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item link @click="/* attach */">
                      <v-list-item-title>
                        {{ $t("_attacher_a_un_groupe_") }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <installation-field v-else :column="column.key" :item="item" />
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="importDialog" max-width="550px" persistent>
      <v-card>
        <v-card-title class="blue darken white--text">{{
          $t("_importer_donnees_sites_")
        }}</v-card-title>
        <v-card-text class="pt-4">
          <p class="mb-4">{{ $t("_selectionner_excel_") }}</p>

          <v-file-input
            v-model="selectedFile"
            :label="$t('_fichier_excel_')"
            accept=".xlsx, .xls"
            prepend-icon="mdi-microsoft-excel"
            outlined
            dense
            show-size
            :placeholder="$t('_cliquez_pour_choisir_un_fichier_')"
          ></v-file-input>

          <v-fade-transition>
            <div v-if="selectedFile" class="mt-2">
              <v-alert border="left" colored-border type="info" elevation="1">
                <strong> {{ $t("_pret_pour_l_import_") }}: </strong>
                {{ selectedFile.name }}
              </v-alert>
            </div>
          </v-fade-transition>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeImportPopup">{{ $t("_annuler_") }}</v-btn>
          <v-btn
            color="primary"
            :loading="importLoading"
            @click="submitExcelImport"
            >{{ $t("_valider_") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <PopUpCreation
      v-if="createAudit"
      type="create"
      :data="dataToGive"
      @close="toggleModal"
    />
    <popUpAddGroup
      v-if="openAddGroup"
      @save="addGroup($event)"
      @close="toggleGroup"
    >
    </popUpAddGroup>
    <popUpRenameGroup
      v-if="openRenameGroup"
      :initial-name="selectedGroup?.name"
      @save="renameGroup($event)"
      @close="toggleRenameGroup"
    >
    </popUpRenameGroup>
    <PopUpGetBrouillon
      v-if="openCreate"
      @close="toggleCreate"
      @new="openNewAudit"
      @use="useDraft($event)"
    />
  </dashboard-main-layout>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import api from "@/api/api";
import Swal from "sweetalert2";
import DashboardMainLayout from "@/layouts/Dashboard/MainLayout";
import InstallationField from "@/components/Table/InstallationField";
import PopUpCreation from "@/components/Creation/PopUpCreation.vue";
import PopUpGetBrouillon from "@/components/Creation/PopUpGetBrouillon.vue";
import popUpAddGroup from "@/components/Creation/popUpAddGroup.vue";
import popUpRenameGroup from "@/components/Creation/popUpRenameGroup.vue";
import MyDashboardComponent from "./dashboard_management.vue";

export default {
  components: {
    DashboardMainLayout,
    InstallationField,
    PopUpCreation,
    PopUpGetBrouillon,
    MyDashboardComponent,
    popUpAddGroup,
    popUpRenameGroup,
  },
  data() {
    return {
      importDialog: false,
      selectedFile: null,
      importLoading: false,
      loading: false,
      search: "",
      openCreate: false,
      openAddGroup: false,
      openRenameGroup: false,
      createAudit: false,
      dataToGive: null,
      selectedInstalls: [],
      sitesLive: [],
      allGroups: [],
      selectedGroup: null,
      hardcodedHeaders: [],
    };
  },
  mounted() {
    this.getInstallationList();
    this.refresh_header_values();
    this.getAllGroups();
  },
  computed: {
    ...mapGetters({
      cur_install_id: "cur_install_id",
      installations: "installations",
      role: "role",
    }),
    selectedColumnsOrdered() {
      return this.hardcodedHeaders
        .filter((column) => {
          // hide for non-admins
          if (
            ["actions", "description"].includes(column.key) &&
            this.role !== "admin"
          ) {
            return false;
          }
          return true;
        })
        .map((c) => ({ ...c, text: this.$t(c.text) }));
    },
    areSelected() {
      return (
        this.sitesLive.length > 0 && this.sitesLive.every((i) => i.checked)
      );
    },
  },
  methods: {
    ...mapMutations(["SET_CUR_INSTALL_ID"]),
    refresh_header_values() {
      this.hardcodedHeaders = [
        { text: "_nom_", value: "installation_name", key: "installation_name" },
        { text: "_company_name_", value: "company_name", key: "company_name" },
        { text: "_commune_", value: "city", key: "city" },
        { text: "_groupe_", value: "groupe", key: "groupe" },
        { text: "_statut_", value: "status", key: "status" },
        {
          text: "_date_soumission_",
          value: "audit_start_date",
          key: "audit_start_date",
        },
        {
          text: "_date_live_",
          value: "daily_follow_up_date",
          key: "daily_follow_up_date",
        },
        {
          text: "_puissance_crete_(kwc)_",
          value: "installed_power_kwc",
          key: "installed_power_kwc",
        },
        {
          text: "_date_fin_abonnement_",
          value: "subscription_end_date",
          key: "subscription_end_date",
        },
        {
          text: "_type_d_installation_",
          value: "installation_type",
          key: "installation_type",
        },
        {
          text: "_actions_",
          value: "actions",
          key: "actions",
          sortable: false,
        },
        { text: "_description_", value: "description", key: "description" },
      ];
    },

    async reprendreBrouillon(item) {
      this.loading = true; // Active le loader pendant l'appel API
      try {
        // 1. On récupère le brouillon via l'API
        // On utilise l'ID d'installation (operations_install_id) lié à la ligne du tableau
        const response = await api.get_draft_by_id(item.operations_install_id);

        // On suppose que la structure de l'API renvoie { data: { reponse: { ... } } }
        // ou directement { reponse: { ... } } selon votre console.log
        const draft = response.reponse || response.data?.reponse;

        if (draft) {
          // 2. On formate l'objet exactement comme PopUpCreation l'attend
          this.dataToGive = {
            id: draft.id, // ID du brouillon en base
            name: draft.name,
            install_id: item.operations_install_id,
            json_data: draft.json_data, // Contient description, rampants, onduleurs, etc.
            configuration_type:
              draft.json_data?.configuration_type || "onduleur",
          };

          // 3. On ouvre la modale de création
          this.createAudit = true;
        } else {
          console.warn("Aucun brouillon trouvé pour cette installation");
          // Optionnel : créer un nouveau dossier si aucun brouillon n'existe
          this.openNewAudit();
        }
      } catch (e) {
        console.error("Erreur lors de la reprise du brouillon :", e);
      } finally {
        this.loading = false;
      }
    },

    async addGroup(name) {
      if (!name.trim()) {
        Swal.fire({
          icon: "error",
          title: "Information Missing",
          text: this.$t("_name_is_required_"),
        });
        return;
      }

      try {
        const res = await api.create_new_group(name);

        const msg = res.data.status.toLowerCase(); // check the backend message

        if (msg.includes("created")) {
          Swal.fire({
            icon: "success",
            title: this.$t("_success_"),
            text: this.$t("_the_group_has_been_created_"),
          });
          this.openAddGroup = false;
        } else if (msg.includes("already exists")) {
          Swal.fire({
            icon: "warning",
            title: this.$t("_warning_"),
            text: this.$t("_this_group_name_already_exists_"),
          });
        } else {
          Swal.fire({
            icon: "error",
            title: this.$t("_error_"),
            text: this.$t("_unknown_response_from_server_"),
          });
        }
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$t("_error_"),
          text: this.$t("_error_creating_group_"),
        });
      } finally {
        this.getAllGroups();
      }
    },

    async renameGroup(newName) {
      if (!this.selectedGroup) return;

      const obj = {
        group_id: this.selectedGroup.id,
        group_name: newName,
      };

      try {
        await api.updateGroup(obj);
        Swal.fire({
          icon: "success",
          title: this.$t("_success_"),
          text: this.$t("_group_renamed_successfully_"),
        });
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$t("_error_"),
          text: this.$t("_error_naming_group_"),
        });
      } finally {
        this.getAllGroups();
        this.getInstallationList();
      }
    },
    async deleteGroup(id) {
      try {
        await api.deleteGroup(id);
        Swal.fire({
          icon: "success",
          title: this.$t("_success_"),
          text: this.$t("_group_has_been_deleted_successfully_"),
        });
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$t("_error_"),
          text: this.$t("_error_deleting_group_"),
        });
      } finally {
        this.getAllGroups();
        this.getInstallationList();
      }
    },

    async getAllGroups() {
      const res = await api.getAllGroups();
      this.allGroups = res.data;
    },

    async addInstallToGroup(groupId) {
      if (!this.selectedInstalls.length) {
        Swal.fire({
          icon: "warning",
          title: this.$t("_warning_"),
          text: this.$t("_no_installations_selected_"),
        });
        return;
      }

      const liveInstalls = this.selectedInstalls.filter(
        (i) => i.status === "live"
      );

      if (!liveInstalls.length) {
        Swal.fire({
          icon: "warning",
          title: this.$t("_warning_"),
          text: this.$t("_only_live_installations_can_be_added_to_a_group_"),
        });
        return;
      }

      const installIds = liveInstalls.map((i) => i.id);

      try {
        await api.addInstallationToGroup({
          group_id: groupId.id,
          installation_ids: installIds,
        });

        Swal.fire({
          icon: "success",
          title: this.$t("_success_"),
          text: this.$t("_installations_added_to_group_"),
        });

        this.selectedInstalls = []; // reset selection
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$t("_error_"),
          text: this.$t("_error_adding_installations_to_group_"),
        });
      } finally {
        this.getInstallationList();
      }
    },

    async removeInstallFromGroup(group_id) {
      if (!this.selectedInstalls.length) {
        Swal.fire({
          icon: "warning",
          title: this.$t("_warning_"),
          text: this.$t("_no_installations_selected_"),
        });
        return;
      }

      const liveInstalls = this.selectedInstalls.filter(
        (i) => i.status === "live"
      );

      if (!liveInstalls.length) {
        Swal.fire({
          icon: "warning",
          title: this.$t("_warning_"),
          text: this.$t(
            "_only_live_installations_can_be_removed_from_a_group_"
          ),
        });
        return;
      }

      const installIds = liveInstalls.map((i) => i.id);

      try {
        await api.deleteInstallationFromGroup({
          group_id: group_id.id,
          installation_ids: installIds,
        });

        Swal.fire({
          icon: "success",
          title: this.$t("_success_"),
          text: this.$t("_installations_removed_from_group_"),
        });

        this.selectedInstalls = []; // reset selection
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$t("_error_"),
          text: this.$t("_error_removing_installations_from_group_"),
        });
      } finally {        
        this.getInstallationList();
      }
    },

    async submitExcelImport() {
      this.importDialog = false;
      this.importLoading = true;
      try {
        if (this.selectedFile) {
          await api.convertsiteexport(this.selectedFile);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.importLoading = false;
      }
    },

    async getInstallationList() {
      this.loading = true;
      try {
        const response = await api.getAllCompanyOperation();
        this.sitesLive = (response.data || []).map((item) => ({
          ...item,
          id: item.installation_id,
          installation_name: item.name,
          status: item.statut,
          audit_start_date: item.submission_date,
          installed_power_kwc: item.puissance_crete,
        }));
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    getStatusChipData(item) {
      if (item.status === "initialisation")
        return { color: "yellow darken-1", text: "Initialisation" };
      if (item.status === "live")
        return { color: "green lighten-1", text: "live" };
      if (item.status === "error d'initialisation")
        return { color: "red darken-1", text: "Erreur d'initialisation" };
      return { color: "blue-grey lighten-2", text: item.status || "N/A" };
    },

    // toggleSelectAll() {
    //   const val = !this.areSelected;
    //   this.sitesLive.forEach((i) => (i.checked = val));
    // },

    // toggleItemSelection(item) {
    //   item.checked = !item.checked;
    // },

    goToInstallation(item) {       
      if (item.status === "live"){   
        this.$store.commit("SET_CUR_INSTALL_ID", item.id);
        this.$router.push(`/installations/show/${item.id}`);
      }
    },

    closeImportPopup() {
      this.importDialog = false;
      this.selectedFile = null;
    },
    toggleCreate() {
      this.openCreate = !this.openCreate;
    },
    toggleGroup() {
      this.openAddGroup = !this.openAddGroup;
    },
    toggleRenameGroup(group = null) {
      this.selectedGroup = group;
      this.openRenameGroup = !this.openRenameGroup;
    },
    openNewAudit() {
      this.dataToGive = null;
      this.createAudit = true;
      this.openCreate = false;
    },
    async useDraft(b) {
      this.dataToGive = b;
      this.createAudit = true;
      this.openCreate = false;
    },
    async toggleModal() {
      this.createAudit = false;
      await this.getInstallationList();
    },
  },
};
</script>

<style>
#level-1-table td {
  padding-top: 20px;
  padding-bottom: 20px;
}
.search {
  max-width: 100%;
}
.v-data-table thead th:first-child {
  border-top-left-radius: 10px;
}

.v-data-table thead th:last-child {
  border-top-right-radius: 10px;
}

.v-data-table thead th {
  background-color: #e0e0e0;
}

.search .v-input__control {
  background-color: #f1f3f4;
  border-radius: 999px !important;
  padding-left: 16px;
  padding-right: 16px;
  box-shadow: 0 1px 6px rgba(32, 33, 36, 0.15);
  transition: all 0.2s ease;
}

.search .v-input__control:hover {
  box-shadow: 0 2px 10px rgba(32, 33, 36, 0.2);
}

.search input {
  padding: 14px 8px;
}
</style>
